// src/components/auth/AuthScreen.jsx
// 로그인 / 회원가입 / 객실 등록 온보딩

import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { firebaseAnonymousLogin } from '../../services/firebase';

// ─── Firebase 에러 코드 → 한국어 메시지 변환 ───
function getAuthErrorMessage(error) {
  const messages = {
    'auth/user-not-found':        '등록되지 않은 이메일입니다.',
    'auth/wrong-password':        '비밀번호가 올바르지 않습니다.',
    'auth/invalid-email':         '이메일 형식이 올바르지 않습니다.',
    'auth/invalid-credential':    '이메일 또는 비밀번호가 올바르지 않습니다.',
    'auth/email-already-in-use':  '이미 사용 중인 이메일입니다.',
    'auth/weak-password':         '비밀번호는 6자 이상이어야 합니다.',
    'auth/too-many-requests':     '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.',
    'auth/network-request-failed':'네트워크 연결을 확인해주세요.',
    'auth/user-disabled':         '비활성화된 계정입니다. 관리자에게 문의해주세요.',
  };
  return messages[error?.code] || '오류가 발생했습니다. 다시 시도해주세요.';
}

// ─── 단계: login | register | roomSetup ───
export default function AuthScreen() {
  const [step, setStep] = useState('login');

  if (step === 'login') return <LoginForm onGoRegister={() => setStep('register')} />;
  if (step === 'register') return <RegisterForm onGoLogin={() => setStep('login')} onSuccess={() => setStep('roomSetup')} />;
  if (step === 'roomSetup') return <RoomSetupForm />;
}

// ─── 로그인 ───
function LoginForm({ onGoRegister }) {
  const { login } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) { setError('이메일과 비밀번호를 입력해주세요.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleDemoLogin() {
    setLoading(true);
    setError('');
    try {
      await firebaseAnonymousLogin();
    } catch {
      setError('잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card" style={{ padding: '28px 22px 24px', gap: 0 }}>

        {/* 1) 로고 + 서비스명 */}
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 36, lineHeight: 1, marginBottom: 6 }}>🏡</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>스테이메이트</h1>
        </div>

        {/* 2) 핵심 가치 한 줄 */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#15803d', margin: '0 0 4px' }}>
            예약 문자 붙여넣기만 하면 자동으로 메시지 발송
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
            체크인·퇴실·홍보까지 한 번에
          </p>
        </div>

        {/* 3) 핵심 기능 3개 */}
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 10,
          padding: '10px 12px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}>
          {[
            '예약 문자 붙여넣기 → 자동 고객 등록',
            '체크인·매너타임 안내 메시지 자동 발송',
            '재방문 고객 추천 및 홍보 메시지',
          ].map((text, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, color: '#15803d' }}>
              <span style={{ fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✔</span>
              <span style={{ lineHeight: 1.4 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* 4) 메인 CTA */}
        <button
          onClick={handleDemoLogin}
          disabled={loading}
          style={{
            width: '100%',
            background: loading
              ? 'var(--green-light)'
              : 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
            border: 'none',
            borderRadius: 14,
            padding: '16px',
            fontSize: 17,
            fontWeight: 800,
            color: loading ? 'var(--green-dark)' : 'white',
            cursor: loading ? 'default' : 'pointer',
            boxShadow: loading ? 'none' : '0 6px 20px rgba(22,163,74,0.45)',
            marginBottom: 8,
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            letterSpacing: '-0.2px',
          }}
        >
          {loading
            ? <><div className="spinner" style={{ borderColor: 'var(--green)', borderTopColor: 'transparent' }} /> 접속 중...</>
            : '👉 바로 체험하기'}
        </button>

        {/* 보조 문구 */}
        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', margin: '0 0 14px' }}>
          회원가입 없이 바로 둘러보기
        </p>

        {/* 5) 서브 버튼 — 무료 시작 */}
        <button
          onClick={onGoRegister}
          style={{
            width: '100%',
            background: 'none',
            border: '1.5px solid #22c55e',
            borderRadius: 12,
            padding: '12px',
            fontSize: 14,
            fontWeight: 700,
            color: '#15803d',
            cursor: 'pointer',
            marginBottom: 18,
            transition: 'all 0.15s',
          }}
        >
          회원가입
        </button>

        {/* 6) 로그인 아코디언 */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <button
            onClick={() => { setShowLoginForm(v => !v); setError(''); }}
            style={{
              background: 'none',
              border: 'none',
              width: '100%',
              textAlign: 'center',
              fontSize: 13,
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            이미 계정이 있으신가요?{' '}
            <span style={{ color: 'var(--green-dark)', fontWeight: 700 }}>로그인</span>
            <span style={{ fontSize: 10, transform: showLoginForm ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>▼</span>
          </button>

          {showLoginForm && (
            <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
              {error && <div className="auth-error">{error}</div>}
              <div className="input-group">
                <label className="input-label">이메일</label>
                <input
                  className="input"
                  type="email"
                  placeholder="example@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div className="input-group">
                <label className="input-label">비밀번호</label>
                <input
                  className="input"
                  type="password"
                  placeholder="비밀번호 입력"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <button
                className="btn btn-green btn-block"
                type="submit"
                disabled={loading}
                style={{ marginTop: 8, padding: 12, fontSize: 14 }}
              >
                {loading ? '로그인 중...' : '로그인'}
              </button>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── 회원가입 ───
function RegisterForm({ onGoLogin, onSuccess }) {
  const { register } = useApp();
  const [form, setForm] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function updateForm(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const { email, password, passwordConfirm, phone } = form;

    if (!email || !password || !phone) {
      setError('모든 항목을 입력해주세요.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await register({ email, password, pensionName: '', phone });
      onSuccess();
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card" style={{ padding: '28px 22px 24px' }}>

        {/* 헤더 */}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 6 }}>🏡</div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' }}>회원가입</h1>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>최소 정보만 입력하고 바로 사용하세요</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}

          {/* 이메일 */}
          <div className="input-group">
            <label className="input-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>이메일</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>로그인에 사용됩니다</span>
            </label>
            <input
              className="input"
              type="email"
              placeholder="example@email.com"
              value={form.email}
              onChange={e => updateForm('email', e.target.value)}
              autoComplete="email"
            />
          </div>

          {/* 연락처 */}
          <div className="input-group">
            <label className="input-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>연락처</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>로그인 및 알림에 사용됩니다</span>
            </label>
            <input
              className="input"
              type="tel"
              placeholder="010-0000-0000"
              value={form.phone}
              onChange={e => updateForm('phone', e.target.value)}
              autoComplete="tel"
            />
          </div>

          {/* 비밀번호 */}
          <div className="input-group">
            <label className="input-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>비밀번호</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>6자 이상 입력해주세요</span>
            </label>
            <input
              className="input"
              type="password"
              placeholder="••••••"
              value={form.password}
              onChange={e => updateForm('password', e.target.value)}
              autoComplete="new-password"
            />
          </div>

          {/* 비밀번호 확인 */}
          <div className="input-group">
            <label className="input-label">비밀번호 확인</label>
            <input
              className="input"
              type="password"
              placeholder="비밀번호 재입력"
              value={form.passwordConfirm}
              onChange={e => updateForm('passwordConfirm', e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <button
            style={{
              width: '100%',
              background: loading
                ? 'var(--green-light)'
                : 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
              border: 'none',
              borderRadius: 14,
              padding: '15px',
              fontSize: 16,
              fontWeight: 800,
              color: loading ? 'var(--green-dark)' : 'white',
              cursor: loading ? 'default' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 16px rgba(22,163,74,0.4)',
              marginTop: 10,
              transition: 'all 0.15s',
            }}
            type="submit"
            disabled={loading}
          >
            {loading ? '가입 중...' : '가입하고 바로 시작하기'}
          </button>
        </form>

        {/* 로그인 링크 */}
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            이미 계정이 있으신가요?{' '}
            <button
              onClick={onGoLogin}
              style={{ background: 'none', border: 'none', color: 'var(--green-dark)', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0 }}
            >
              로그인
            </button>
          </span>
        </div>

      </div>
    </div>
  );
}

// ─── 객실 등록 온보딩 ───
function RoomSetupForm() {
  const { setRooms: saveRoomsToFirestore, showToast } = useApp();
  const [rooms, setRooms] = useState([
    { name: '', capacity: '', checkinTime: '15:00', checkoutTime: '11:00', notes: '' },
  ]);
  const [saving, setSaving] = useState(false);

  function updateRoom(idx, key, value) {
    setRooms(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }

  function addRoomField() {
    setRooms(prev => [...prev, { name: '', capacity: '', checkinTime: '15:00', checkoutTime: '11:00', notes: '' }]);
  }

  function removeRoomField(idx) {
    setRooms(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    const valid = rooms.filter(r => r.name.trim());
    if (valid.length === 0) {
      showToast('객실명을 최소 1개 입력해주세요.', 'error');
      return;
    }
    setSaving(true);

    // 기본 샘플 방을 지우고 새 방으로 교체
    const newRooms = valid.map((r, i) => ({
      id: `room_${Date.now()}_${i}`,
      name: r.name.trim(),
      capacity: parseInt(r.capacity) || 4,
      checkinTime: r.checkinTime || '15:00',
      checkoutTime: r.checkoutTime || '11:00',
      notes: r.notes.trim(),
    }));

    try {
      await saveRoomsToFirestore(newRooms);
      showToast('객실이 등록되었습니다!');
    } catch (e) {
      showToast('저장 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleSkip() {
    // 기본 객실로 진행
    showToast('기본 객실로 설정되었습니다. 나중에 설정에서 변경하세요.');
  }

  return (
    <div className="auth-screen">
      <div className="auth-card" style={{ maxHeight: '90dvh', overflowY: 'auto' }}>
        <div className="auth-logo">
          <div className="auth-logo-icon">🏠</div>
          <h1 className="auth-title">객실 등록</h1>
          <p className="auth-subtitle">운영 중인 객실을 등록해주세요 (최소 1개)</p>
        </div>

        {rooms.map((room, idx) => (
          <div key={idx} className="room-setup-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>객실 {idx + 1}</span>
              {rooms.length > 1 && (
                <button
                  onClick={() => removeRoomField(idx)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}
                >
                  ×
                </button>
              )}
            </div>

            <div className="input-group">
              <label className="input-label">객실명 *</label>
              <input
                className="input"
                placeholder="예: 힐링독채 A"
                value={room.name}
                onChange={e => updateRoom(idx, 'name', e.target.value)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div className="input-group">
                <label className="input-label">최대 인원</label>
                <input
                  className="input"
                  type="number"
                  placeholder="명"
                  value={room.capacity}
                  onChange={e => updateRoom(idx, 'capacity', e.target.value)}
                />
              </div>
              <div className="input-group">
                <label className="input-label">체크인</label>
                <input
                  className="input"
                  placeholder="15:00"
                  value={room.checkinTime}
                  onChange={e => updateRoom(idx, 'checkinTime', e.target.value)}
                />
              </div>
              <div className="input-group">
                <label className="input-label">체크아웃</label>
                <input
                  className="input"
                  placeholder="11:00"
                  value={room.checkoutTime}
                  onChange={e => updateRoom(idx, 'checkoutTime', e.target.value)}
                />
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">안내사항</label>
              <textarea
                className="input"
                placeholder="주차, Wi-Fi, 바비큐 등 안내할 내용"
                value={room.notes}
                onChange={e => updateRoom(idx, 'notes', e.target.value)}
                style={{ minHeight: 60, resize: 'none' }}
              />
            </div>
          </div>
        ))}

        <button
          className="btn btn-outline btn-block"
          onClick={addRoomField}
          style={{ marginBottom: 12 }}
        >
          + 객실 추가
        </button>

        <button
          className="btn btn-green btn-block"
          onClick={handleSave}
          disabled={saving}
          style={{ padding: 16, fontSize: 16 }}
        >
          {saving ? '저장 중...' : '저장하고 시작하기'}
        </button>

        <button
          className="btn btn-outline btn-block"
          onClick={handleSkip}
          style={{ marginTop: 8, color: 'var(--text-muted)' }}
        >
          나중에 설정하기
        </button>
      </div>
    </div>
  );
}
